import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { baseApiURL } from '../../baseUrl';
import { FiBriefcase, FiCalendar, FiFileText, FiUser, FiTrendingUp, FiCheckCircle } from 'react-icons/fi';

const Dashboard = () => {
    const [details, setDetails] = useState(null);
    const [stats, setStats] = useState({
        companies: 0,
        drives: 0,
        applications: 0,
        selected: 0,
        students: 0,
    });
    const [loadingStats, setLoadingStats] = useState(true);

    useEffect(() => {
        fetchDetails();
        fetchStats();
    }, []);

    const fetchDetails = async () => {
        try {
            const res = await axios.post(`${baseApiURL()}/placement/details/getDetails`, {}, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            if (res.data.success && res.data.user?.length > 0) {
                setDetails(res.data.user[0]);
            }
        } catch (error) {
            console.error('Error fetching details', error);
        }
    };

    const fetchStats = async () => {
        setLoadingStats(true);
        try {
            const [compRes, driveRes] = await Promise.all([
                axios.get(`${baseApiURL()}/placement/companies/get`),
                axios.get(`${baseApiURL()}/placement/drives/get`),
            ]);

            const drives = driveRes.data.drives || [];
            let totalApplications = 0;
            let totalSelected = 0;

            // Fetch applications per drive
            const appPromises = drives.map(d =>
                axios.get(`${baseApiURL()}/placement/applications/drive/${d._id}`).catch(() => null)
            );
            const appResults = await Promise.all(appPromises);
            appResults.forEach(res => {
                if (res?.data?.success) {
                    totalApplications += res.data.applications?.length || 0;
                    totalSelected += (res.data.applications || []).filter(a => a.status === 'Selected').length;
                }
            });

            // Fetch student count
            let studentCount = 0;
            try {
                const studRes = await axios.get(`${baseApiURL()}/placement/studentprofiles/get`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                });
                if (studRes.data.success) studentCount = studRes.data.total || 0;
            } catch (e) { /* ignore */ }

            setStats({
                companies: compRes.data.companies?.length || 0,
                drives: drives.length,
                applications: totalApplications,
                selected: totalSelected,
                students: studentCount,
            });
        } catch (error) {
            console.error('Error fetching stats', error);
        } finally {
            setLoadingStats(false);
        }
    };

    const placementRate = stats.students > 0
        ? ((stats.selected / stats.students) * 100).toFixed(1)
        : 0;

    return (
        <div className="space-y-6">
            <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs overflow-hidden">
                <div className="bento-header-banner flex items-center gap-3">
                    <div className="bg-indigo-50 dark:bg-indigo-950/60 border border-indigo-100 dark:border-indigo-800 p-2 rounded-xl text-indigo-600 dark:text-indigo-400">
                        <FiBriefcase className="text-xl" />
                    </div>
                    <div>
                        <h1 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white">Placement Overview</h1>
                        <p className="text-slate-500 dark:text-slate-400 text-xs mt-0.5">
                            Real-time statistics on recruitment drives, partner companies, and student placements.
                        </p>
                    </div>
                </div>

                <div className="p-6 space-y-6">
                    {/* Placement In-Charge Details */}
                    {details && (
                        <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 rounded-2xl p-6 text-white relative overflow-hidden border border-slate-800 shadow-sm">
                            <div className="absolute right-0 top-0 -mt-8 -mr-8 w-40 h-40 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none"></div>
                            <div className="flex items-center gap-2 mb-4">
                                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold bg-amber-500/20 text-amber-300 border border-amber-500/30">
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 mr-1.5 animate-pulse"></span>
                                    In-Charge Details
                                </span>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                                <div className="p-3 bg-white/5 rounded-xl border border-white/10">
                                    <p className="text-indigo-200/70 text-[10px] uppercase font-bold tracking-wider">Name</p>
                                    <p className="text-xs font-bold text-white mt-0.5">{details.firstName} {details.middleName} {details.lastName}</p>
                                </div>
                                <div className="p-3 bg-white/5 rounded-xl border border-white/10">
                                    <p className="text-indigo-200/70 text-[10px] uppercase font-bold tracking-wider">Designation</p>
                                    <p className="text-xs font-bold text-white mt-0.5">{details.designation || "Officer"}</p>
                                </div>
                                <div className="p-3 bg-white/5 rounded-xl border border-white/10">
                                    <p className="text-indigo-200/70 text-[10px] uppercase font-bold tracking-wider">Employee ID</p>
                                    <p className="text-xs font-bold font-mono text-white mt-0.5">{details.employeeId}</p>
                                </div>
                                <div className="p-3 bg-white/5 rounded-xl border border-white/10">
                                    <p className="text-indigo-200/70 text-[10px] uppercase font-bold tracking-wider">Email</p>
                                    <p className="text-xs font-bold text-white mt-0.5 truncate">{details.email}</p>
                                </div>
                                <div className="p-3 bg-white/5 rounded-xl border border-white/10">
                                    <p className="text-indigo-200/70 text-[10px] uppercase font-bold tracking-wider">Phone</p>
                                    <p className="text-xs font-bold text-white mt-0.5">{details.phoneNumber || "N/A"}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                        {/* Companies */}
                        <div className="p-5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl flex flex-col justify-between gap-3">
                            <div>
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Companies</p>
                                <p className="text-3xl font-bold text-slate-900 dark:text-white mt-1">
                                    {loadingStats ? <span className="text-slate-300 dark:text-slate-600 animate-pulse">...</span> : stats.companies}
                                </p>
                            </div>
                            <div className="p-2.5 bg-emerald-50 dark:bg-emerald-950/60 border border-emerald-200 dark:border-emerald-800 text-emerald-600 dark:text-emerald-400 rounded-xl w-fit">
                                <FiBriefcase className="text-base" />
                            </div>
                        </div>
                        {/* Drives */}
                        <div className="p-5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl flex flex-col justify-between gap-3">
                            <div>
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Drives</p>
                                <p className="text-3xl font-bold text-slate-900 dark:text-white mt-1">
                                    {loadingStats ? <span className="text-slate-300 dark:text-slate-600 animate-pulse">...</span> : stats.drives}
                                </p>
                            </div>
                            <div className="p-2.5 bg-indigo-50 dark:bg-indigo-950/60 border border-indigo-200 dark:border-indigo-800 text-indigo-600 dark:text-indigo-400 rounded-xl w-fit">
                                <FiCalendar className="text-base" />
                            </div>
                        </div>
                        {/* Applications */}
                        <div className="p-5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl flex flex-col justify-between gap-3">
                            <div>
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Applications</p>
                                <p className="text-3xl font-bold text-slate-900 dark:text-white mt-1">
                                    {loadingStats ? <span className="text-slate-300 dark:text-slate-600 animate-pulse">...</span> : stats.applications}
                                </p>
                            </div>
                            <div className="p-2.5 bg-amber-50 dark:bg-amber-950/60 border border-amber-200 dark:border-amber-800 text-amber-600 dark:text-amber-400 rounded-xl w-fit">
                                <FiFileText className="text-base" />
                            </div>
                        </div>
                        {/* Selected */}
                        <div className="p-5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl flex flex-col justify-between gap-3">
                            <div>
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Selected</p>
                                <p className="text-3xl font-bold text-emerald-600 dark:text-emerald-400 mt-1">
                                    {loadingStats ? <span className="text-slate-300 dark:text-slate-600 animate-pulse">...</span> : stats.selected}
                                </p>
                            </div>
                            <div className="p-2.5 bg-emerald-50 dark:bg-emerald-950/60 border border-emerald-200 dark:border-emerald-800 text-emerald-600 dark:text-emerald-400 rounded-xl w-fit">
                                <FiCheckCircle className="text-base" />
                            </div>
                        </div>
                        {/* Placement Rate */}
                        <div className="p-5 bg-gradient-to-br from-indigo-50 to-violet-50 dark:from-indigo-950/40 dark:to-violet-950/40 border border-indigo-200 dark:border-indigo-800/60 rounded-xl flex flex-col justify-between gap-3">
                            <div>
                                <p className="text-[10px] font-bold text-indigo-400 uppercase tracking-wider">Placement Rate</p>
                                <p className="text-3xl font-bold text-indigo-700 dark:text-indigo-300 mt-1">
                                    {loadingStats ? <span className="text-indigo-300 dark:text-indigo-700 animate-pulse">...</span> : `${placementRate}%`}
                                </p>
                                {!loadingStats && (
                                    <p className="text-[10px] text-indigo-400 mt-0.5">{stats.selected} of {stats.students} students</p>
                                )}
                            </div>
                            <div className="p-2.5 bg-indigo-100 dark:bg-indigo-900/60 border border-indigo-200 dark:border-indigo-700 text-indigo-600 dark:text-indigo-400 rounded-xl w-fit">
                                <FiTrendingUp className="text-base" />
                            </div>
                        </div>
                    </div>

                    {/* Placement rate progress bar */}
                    {!loadingStats && stats.students > 0 && (
                        <div className="bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl p-4 space-y-2">
                            <div className="flex items-center justify-between text-xs">
                                <span className="font-bold text-slate-700 dark:text-slate-300">Overall Placement Progress</span>
                                <span className="font-bold text-indigo-600 dark:text-indigo-400">{placementRate}%</span>
                            </div>
                            <div className="h-2 bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-gradient-to-r from-indigo-500 to-violet-500 rounded-full transition-all duration-700"
                                    style={{ width: `${Math.min(placementRate, 100)}%` }}
                                />
                            </div>
                            <p className="text-[10px] text-slate-400">{stats.selected} students placed out of {stats.students} total</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
